import { useAudioApi } from '@App/hooks/useAudioApi';
import { Component, useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import classnames from 'classnames';
import { playSoundEffect, seekTrack } from '@App/store/actions/player';
import Anchor from '@App/components/navigation/Anchor';
import WaveformByUrl from '@App/components/model/WaveformByUrl';
import SoundEffectButtonBar from '@App/components/model/SoundEffectButtonBar';
import { formatDuration } from '@App/utils';
import { SIZE_LG } from '@App/service/MobileWatcher';
import { isLoadingTrack, isPausedTrack, isPlayingTrack } from '@App/store/reducers/player';
import { get } from '@App/container';
import Seeker from '@App/components/ui/Seeker';
import { SoundEffectPropType, SoundEffectData } from '@App/components/model/SoundEffectPropType';

export { SoundEffectPropType };

interface Props {
  sound: SoundEffectData
  onPlay: () => void
  onSeek: (position: number) => void
  onPlayAt: () => void
  mobile: boolean
  active: boolean
  isPlaying?: boolean
  isPaused?: boolean
  isLoading?: boolean
  read: boolean
  playTime: number
}

class SoundEffect extends Component<Props> {
  static propTypes = {
    sound: SoundEffectPropType.isRequired,
    onPlay: PropTypes.func.isRequired,
    onSeek: PropTypes.func.isRequired,
    onPlayAt: PropTypes.func.isRequired,
    mobile: PropTypes.bool.isRequired,
    active: PropTypes.bool.isRequired,
    isPlaying: PropTypes.bool,
    isPaused: PropTypes.bool,
    isLoading: PropTypes.bool,
    read: PropTypes.bool.isRequired,
    playTime: PropTypes.number,
  };

  static defaultProps = {
    isPlaying: false,
    isPaused: false,
    isLoading: false,
    playTime: 0,
  };

  constructor(props: Props) {
    super(props);

    this.onPlay = this.onPlay.bind(this);
    this.onSeek = this.onSeek.bind(this);
  }

  onPlay() {
    // Current sound effect loading:
    if (this.props.isLoading) {
      return;
    }

    // Current sound effect is loaded, allow to pause/play:
    if (this.props.isPlaying || this.props.isPaused) {
      get('audio').togglePlay();

      return;
    }

    // Otherwise, load & play the preview:
    this.props.onPlay();
  }

  onSeek(position: number) {
    if (this.props.isLoading) {
      return;
    }

    // Seek is only meaningful once the preview is loaded; otherwise just start it.
    if (this.props.isPlaying || this.props.isPaused) {
      this.props.onSeek(position * this.props.sound.duration);

      return;
    }

    this.props.onPlayAt();
  }

  render() {
    const {
      sound,
      mobile,
      active,
      isPlaying,
      isPaused,
      isLoading,
      read,
      playTime,
    } = this.props;
    const {
      id,
      title,
      category,
      subCategory,
      duration,
      previewUrl,
      waveformDataUrl,
      waveformImageUrl,
      artworkUrl,
    } = sound;

    const subtitle = [category, subCategory].filter(Boolean).join(' · ');

    return (
      <Anchor className={classnames('track', 'track--sound-effect', {
        'track--active': isPlaying,
        'track--paused': isPaused,
        'track--loading': isLoading,
        'track--read': read && !(isPlaying || isPaused || isLoading),
      })} id={id} active={active}>
        <div className="track__main">
          <div className="track__details">
            <button
              type="button"
              className="cover cover--sound-effect"
              style={artworkUrl ? { backgroundImage: `url('${artworkUrl}')` } : undefined}
              onClick={this.onPlay}
              aria-hidden="true"
            />
            <div className="details">
              <button type="button" className="title" onClick={this.onPlay}>
                <span className="sr-only">Lire</span>
                {title}
              </button>
              {subtitle ? <span className="info">{subtitle}</span> : null}
            </div>
          </div>
          <span className="track__waveform">
            <Seeker onSeek={this.onSeek} className="seeker">
              <WaveformByUrl reference={id} audioFileUrl={previewUrl} waveformUrl={waveformDataUrl} waveformImageUrl={waveformImageUrl} progress={playTime / duration} duration={duration} light />
            </Seeker>
            <span className="length">
              {formatDuration(duration)}
            </span>
          </span>
          <span className="track__actions">
            <SoundEffectButtonBar mobile={mobile} sound={sound} />
          </span>
        </div>
      </Anchor>
    );
  }
}

function SoundEffectWrapper({ isPlaying, ...remainingProps }: Omit<Props, 'playTime'>) {
  const [playTime, setPlayTime] = useState<number>(0);
  const audio = useAudioApi();

  const handlePlayTimeUpdated = useCallback(() => {
    setPlayTime(audio.getTime());
  }, [audio]);

  /**
   * Track & store the playtime in component state by listening to the audio API events.
   */
  useEffect(() => {
    if (isPlaying) {
      audio.addEventListener('timeupdate', handlePlayTimeUpdated);
    }

    return () => audio.removeEventListener('timeupdate', handlePlayTimeUpdated);
  }, [audio, handlePlayTimeUpdated, isPlaying]);

  return <SoundEffect {...remainingProps} isPlaying={isPlaying} playTime={playTime} />;
}

SoundEffectWrapper.propTypes = {
  isPlaying: PropTypes.bool.isRequired,
};

/**
 * Build the flat payload consumed by the player (no server round-trip).
 */
function toPlayerSoundEffect(sound: SoundEffectData) {
  return {
    id: sound.id,
    previewUrl: sound.previewUrl,
    title: sound.title,
    duration: sound.duration,
    waveformDataUrl: sound.waveformDataUrl,
    waveformImageUrl: sound.waveformImageUrl,
    artworkUrl: sound.artworkUrl,
    // Kept so the player bar's own SoundEffectButtonBar (Player.renderButtonBar) still has the
    // download link: it reads sound.downloadUrl now, and this payload does not come from GraphQL.
    downloadUrl: sound.downloadUrl,
  };
}

export default connect(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (state: any, props: any) => ({
    mobile: state.navigation.size < SIZE_LG,
    active: state.navigation.anchor === props.sound.id,
    isPlaying: isPlayingTrack(state, props.sound.id),
    isPaused: isPausedTrack(state, props.sound.id),
    isLoading: isLoadingTrack(state, props.sound.id),
    read: state.player.history.includes(props.sound.id),
  }),
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (dispatch, props: any) => ({
    onPlay: () => dispatch(playSoundEffect(toPlayerSoundEffect(props.sound))),
    onSeek: (offset) => dispatch(seekTrack(offset)),
    onPlayAt: () => dispatch(playSoundEffect(toPlayerSoundEffect(props.sound))),
  }),
)(SoundEffectWrapper);
